import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CityStatusCard from '../components/xtamDashboard/CityStatusCard';
import DashboardUsabilityCharts from '../components/xtamDashboard/DashboardUsabilityCharts';
import XTAMSpinner from '../components/XTAMSpinner';
import {
  aggregateCityStats,
  fetchCitySitesAndCams,
  fetchXtamDashboardClients,
  type CityOperationalStats,
  type XtamDashClientRef,
} from '../services/xtamDashboardsApi';
import './DashboardPage.css';

const EMPTY_STATS: CityOperationalStats = {
  sitesTotal: 0,
  sitesOn: 0,
  sitesOff: 0,
  camsTotal: 0,
  camsOn: 0,
  camsOff: 0,
  sitesPct: 0,
  camsPct: 0,
};

const RETRY_MS = 60_000;

type CityRowState = {
  online: boolean;
  stats: CityOperationalStats;
  lastUpdated: string | null;
  error: string | null;
  loading: boolean;
};

const DashboardPage: React.FC = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState<XtamDashClientRef[]>([]);
  const [cityState, setCityState] = useState<Record<string, CityRowState>>({});
  const [loadingClients, setLoadingClients] = useState(true);

  const loadClient = useCallback(
    async (client: XtamDashClientRef) => {
      if (!token) return;
      setCityState((prev) => ({
        ...prev,
        [client.key]: {
          ...(prev[client.key] ?? {
            online: false,
            stats: EMPTY_STATS,
            lastUpdated: null,
            error: null,
            loading: true,
          }),
          loading: true,
        },
      }));
      try {
        const payload = await fetchCitySitesAndCams(token, client.key);
        const stats = aggregateCityStats(payload);
        const now = new Date().toLocaleString('es-CO', { hour: '2-digit', minute: '2-digit' });
        setCityState((prev) => ({
          ...prev,
          [client.key]: {
            online: true,
            stats,
            lastUpdated: now,
            error: null,
            loading: false,
          },
        }));
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data
            ?.error ||
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (e as Error)?.message ||
          'Sin conexión con la ciudad.';
        setCityState((prev) => ({
          ...prev,
          [client.key]: {
            online: false,
            stats: prev[client.key]?.stats ?? EMPTY_STATS,
            lastUpdated: prev[client.key]?.lastUpdated ?? null,
            error: msg,
            loading: false,
          },
        }));
      }
    },
    [token]
  );

  const refreshAll = useCallback(async () => {
    if (!token || clients.length === 0) return;
    await Promise.all(clients.map((c) => loadClient(c)));
  }, [token, clients, loadClient]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoadingClients(true);
      try {
        const list = await fetchXtamDashboardClients(token);
        if (!cancelled) setClients(list);
      } catch {
        if (!cancelled) setClients([{ key: 'LOCAL', label: 'LOCAL' }]);
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || clients.length === 0) return;
    void refreshAll();
    const id = window.setInterval(() => void refreshAll(), RETRY_MS);
    return () => window.clearInterval(id);
  }, [token, clients, refreshAll]);

  const sectionTitle = useMemo(
    () => '1 · RESUMEN estado actual por ciudad',
    []
  );

  return (
    <div className="dashboard-container dashboard-container--xtam">
      <div className="dashboard-content">
        <div className="dashboard-xtam-header">
          <div>
            <h1 className="dashboard-xtam-title">{sectionTitle}</h1>
            <p className="dashboard-xtam-subtitle">
              Disponibilidad de sitios y cámaras por ciudad (actualización automática cada 60 s)
            </p>
          </div>
          <button
            type="button"
            className="dashboard-xtam-refresh"
            onClick={() => void refreshAll()}
            disabled={!token || loadingClients}
            title="Actualizar ahora"
          >
            <RefreshCw size={18} aria-hidden />
            Actualizar
          </button>
        </div>

        {loadingClients ? (
          <XTAMSpinner variant="inline" message="Cargando ciudades..." />
        ) : (
          <div className="dashboard-xtam-row" role="list">
            {clients.map((client) => {
              const row = cityState[client.key] ?? {
                online: false,
                stats: EMPTY_STATS,
                lastUpdated: null,
                error: null,
                loading: true,
              };
              return (
                <div key={client.key} role="listitem" className="dashboard-xtam-row__item">
                  <CityStatusCard
                    cityLabel={client.label}
                    online={row.online}
                    stats={row.stats}
                    lastUpdated={row.lastUpdated}
                    loading={row.loading}
                    errorMessage={row.error}
                    onRetry={() => void loadClient(client)}
                  />
                </div>
              );
            })}
          </div>
        )}

        <DashboardUsabilityCharts token={token} />
      </div>
    </div>
  );
};

export default DashboardPage;
